<template>
  <div>
    <b-card>
      <b-card-title>
        <h2>Personal Information</h2>
      </b-card-title>
      <b-row>
        <!-- User Info: Left col -->
        <b-col
          cols="21"
          xl="6"
          class="d-flex flex-column"
        >
          <!-- User Avatar & Action Buttons -->
          <div class="d-flex justify-content-start">
            <div
              v-if="loadingPic"
              class="d-flex justify-content-center align-items-center"
              style="width:95px;height:95px"
            >
              <b-spinner />
            </div>
            <b-avatar
              v-else
              :src="pic"
              :text="avatarText(driverData.fullName)"
              variant="light-danger"
              size="95px"
              rounded
            />
            <div class="d-flex flex-column ml-1">
              <h4 class="mb-0">
                {{ driverData.fullName }}
              </h4>
              <div
                v-if="$can('write', 'drivers')"
                class="d-flex flex-wrap"
              >
                <b-button
                  :to="{ name: 'drivers-edit', params: { id: driverData.id } }"
                  variant="primary"
                  class="mt-1"
                >
                  Edit
                </b-button>
              </div>
            </div>
          </div>
          <table class="mt-1">
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="ToggleRightIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Status</span>
              </th>
              <td class="pb-50">
                {{ driverData.activeState == 'INACTIVE' ? inactivationPriority(driverData.inactivationReasons) : 'Approved' }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="MailIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Email</span>
              </th>
              <td class="pb-50">
                {{ driverData.email }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="UserIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Gender</span>
              </th>
              <td class="pb-50">
                {{ driverData.gender }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="StarIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Rating</span>
              </th>
              <td class="pb-50">
                {{ driverRating }}
              </td>
            </tr>
            <tr v-if="driverData.addedByAdmin">
              <th class="pb-50">
                <feather-icon
                  icon="StarIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Added By</span>
              </th>
              <td class="pb-50">
                {{ driverData.addedByAdmin }}
              </td>
            </tr>

          </table>
        </b-col>

        <!-- Right Col: Table -->
        <b-col
          cols="12"
          xl="6"
        >
          <table class="mt-2 mt-xl-0 w-100">
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="ActivityIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Acceptance ratio</span>
              </th>
              <td class="pb-50">
                {{ (!driverData.acceptanceRatio || (driverData.acceptanceRatio == 'None')) ? '100' : driverData.acceptanceRatio }} %
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="CheckIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Last OTP</span>
              </th>
              <td class="pb-50 text-capitalize">
                <b-button
                  variant="primary"
                  pill
                  size="sm"
                  :disabled="loadingOTP"
                  @click="getLastOTP"
                >
                  Get OTP
                </b-button>
                <span v-if="driverLastOTP"> / {{ driverLastOTP }}</span>
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="StarIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">National ID</span>
              </th>
              <td class="pb-50 text-capitalize">
                {{ driverData.nationalId }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="FlagIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Country</span>
              </th>
              <td class="pb-50">
                {{ driverData.country }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="FlagIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">City</span>
              </th>
              <td class="pb-50">
                {{ driverData.city }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="FlagIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Address</span>
              </th>
              <td class="pb-50">
                <!-- Address placeholder -->
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="ClockIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Created At</span>
              </th>
              <td class="pb-50">
                {{ driverData.createdAt | formatDate }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="ClockIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">Updated At</span>
              </th>
              <td class="pb-50">
                {{ driverData.updatedAt | formatDate }}
              </td>
            </tr>
            <tr>
              <th class="pb-50">
                <feather-icon
                  icon="ToggleLeftIcon"
                  class="mr-75"
                />
                <span class="font-weight-bold">IS Online</span>
              </th>
              <b-badge
                :variant="`light-${driverData.status == 'UNAVAILABLE' ? 'danger' : 'success'}`"
              >
                <td
                  class="pb-50"
                >
                  {{ state }}
                </td>
              </b-badge>
            </tr>

          </table>
        </b-col>
        <!-- car info -->
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BButton, BAvatar, BRow, BCol, BCard, BCardTitle, BBadge, BSpinner,
} from 'bootstrap-vue'
import { avatarText } from '@core/utils/filter'
import { computed, ref } from '@vue/composition-api'
import inactivationPriority from '@/views/driver/inactivation-priority'

export default {
  components: {
    BButton,
    BRow,
    BCol,
    BAvatar,
    BCard,
    BCardTitle,
    BBadge,
    BSpinner,
  },

  props: {
    driverData: {
      type: Object,
      default: () => ({}),
    },
    driverRating: {
      type: [Number, String],
      default: null,
    },
  },
  setup(props, ctx) {
    // eslint-disable-next-line consistent-return
    const state = computed(() => {
      if (props.driverData.status === 'AVAILABLE') {
        return 'online'
      }
      if (props.driverData.status === 'UNAVAILABLE') {
        return 'offline'
      }
      if (props.driverData.status === 'OCCUPIED') {
        return 'on trip'
      }
    })
    const driverId = ctx.root.$route.params.id
    const pic = ref(null)
    const loadingPic = ref(false)
    const getDriverProfilePic = async () => {
      loadingPic.value = true
      const { url } = (await ctx.root.$store.dispatch('drivers/getProfilePicture', driverId)).data.data
      pic.value = url
      loadingPic.value = false
    }

    const driverLastOTP = ref(undefined)
    const loadingOTP = ref(false)
    const getLastOTP = async () => {
      loadingOTP.value = true
      const res = (await ctx.root.$store.dispatch('drivers/fetchLastOtp', props.driverData.phoneNumber))
      const result = res?.data?.data?.data?.otp_code
      if (result) {
        driverLastOTP.value = result
      } else {
        driverLastOTP.value = 'Not Found'
      }
      loadingOTP.value = false
    }
    getDriverProfilePic()

    return {
      avatarText,
      state,
      loadingPic,
      pic,
      driverLastOTP,
      getLastOTP,
      loadingOTP,
      inactivationPriority,
    }
  },
}
</script>

<style>

</style>
